<template>
    <ModalContent>
        <ClaimInfoContainer>
            <!-- 모달 헤더 -->
            <ModalHeader>
                <HeaderContent>
                    <HeaderIcon>
                        <InformationOutline :size="35" />
                    </HeaderIcon>
                    <HeaderText>
                        <ModalTitle>청구 정보 입력</ModalTitle>
                    </HeaderText>
                </HeaderContent>
            </ModalHeader>

            <!-- 메인 콘텐츠 -->
            <ClaimInfoMain>
                <!-- 왼쪽: 저장된 청구 정보 목록 -->
                <LeftSection>
                    <TitleWrapper>
                        <DataSectionTitle>청구 이력</DataSectionTitle>
                        <ButtonGroup>
                            <RefreshButton @click="refreshClaimHistory">
                                <RefreshIcon :size="16" />
                                새로고침
                            </RefreshButton>
                            <DeleteAllButton @click="deleteSelectedClaim" :disabled="claimHistory.length === 0">
                                <DeleteIcon :size="16" />
                                삭제
                            </DeleteAllButton>
                        </ButtonGroup>
                    </TitleWrapper>

                    <!-- 청구 이력 목록 -->
                    <ClaimHistoryList>
                        <ClaimHistoryItem v-for="(claim, index) in claimHistory" :key="index"
                            :class="{ 'active': selectedClaimIndex === index }" @click="selectClaim(claim, index)">
                            <ClaimHistoryHeader>
                                <ClaimHistoryTitle>{{ claim.title }}</ClaimHistoryTitle>
                                <ClaimHistoryStatus :status="claim.status">{{ claim.statusText }}</ClaimHistoryStatus>
                            </ClaimHistoryHeader>
                            <ClaimHistoryContent>
                                <ClaimHistoryInfo>
                                    <ClaimHistoryLabel>처방일:</ClaimHistoryLabel>
                                    <ClaimHistoryValue>{{ claim.prescriptionDate }}</ClaimHistoryValue>
                                </ClaimHistoryInfo>
                                <ClaimHistoryInfo>
                                    <ClaimHistoryLabel>등록일:</ClaimHistoryLabel>
                                    <ClaimHistoryValue>{{ claim.createdDate }}</ClaimHistoryValue>
                                </ClaimHistoryInfo>
                                <ClaimHistoryInfo>
                                    <ClaimHistoryLabel>세금계산서 발행여부:</ClaimHistoryLabel>
                                    <ClaimHistoryValue>{{ claim.taxInvoiceIssued }}</ClaimHistoryValue>
                                </ClaimHistoryInfo>
                                <ClaimHistoryInfo>
                                    <ClaimHistoryLabel>현금영수증 발행여부:</ClaimHistoryLabel>
                                    <ClaimHistoryValue>{{ claim.cashReceiptIssued }}</ClaimHistoryValue>
                                </ClaimHistoryInfo>
                            </ClaimHistoryContent>
                        </ClaimHistoryItem>

                        <!-- 청구 이력이 없는 경우 -->
                        <EmptyState v-if="claimHistory.length === 0">
                            <EmptyStateIcon>
                                <FileDocumentIcon :size="48" />
                            </EmptyStateIcon>
                            <EmptyStateTitle>청구 이력이 없습니다</EmptyStateTitle>
                            <EmptyStateDescription>새로운 청구 정보를 등록해보세요</EmptyStateDescription>
                        </EmptyState>
                    </ClaimHistoryList>
                </LeftSection>

                <!-- 오른쪽: 처방전 AI 분석 섹션 -->
                <RightSection>
                    <TitleWrapper>
                        <DataSectionTitle>처방전 AI 분석</DataSectionTitle>
                        <OcrAnalyzeButton v-if="prescriptionFile" @click="analyzePrescription"
                            :disabled="isOcrAnalyzing">
                            <OcrStatusIcon>
                                <FileDocumentIcon :size="20" />
                            </OcrStatusIcon>
                            <OcrStatusText>{{ isOcrAnalyzing ? 'OCR 분석 중...' : 'OCR 분석 시작' }}</OcrStatusText>
                        </OcrAnalyzeButton>
                    </TitleWrapper>

                    <!-- 처방전 파일 업로드 -->
                    <PrescriptionUploadContainer>
                        <PrescriptionDropZone ref="prescriptionDropZone" :class="{
                            'active': isPrescriptionDragging,
                            'has-file': prescriptionFile
                        }" @dragenter="handlePrescriptionDragEnter" @dragover="handlePrescriptionDragOver"
                            @dragleave="handlePrescriptionDragLeave" @drop="handlePrescriptionDrop"
                            @paste="handlePrescriptionPaste" @click="triggerPrescriptionFileInput" tabindex="0">

                            <PrescriptionUploadArea v-if="!prescriptionFile">
                                <PrescriptionUploadIcon>
                                    <FileDocumentIcon :size="30" />
                                </PrescriptionUploadIcon>
                                <PrescriptionUploadText>
                                    <PrescriptionUploadMainText>파일을 드래그하거나 클릭하여 업로드</PrescriptionUploadMainText>
                                    <PrescriptionUploadSubText>PDF, JPG, PNG 파일 지원</PrescriptionUploadSubText>
                                </PrescriptionUploadText>
                            </PrescriptionUploadArea>

                            <PrescriptionFilePreview v-else>
                                <PrescriptionFileIcon>
                                    <FileDocumentIcon :size="32" />
                                </PrescriptionFileIcon>
                                <PrescriptionFileInfo>
                                    <PrescriptionFileName>{{ prescriptionFile.name }}</PrescriptionFileName>
                                    <PrescriptionFileSize>{{ formatFileSize(prescriptionFile.size) }}
                                    </PrescriptionFileSize>
                                </PrescriptionFileInfo>
                                <PrescriptionFileRemove @click.stop="removePrescriptionFile">
                                    <CloseIcon :size="16" :style="{ paddingTop: '3px' }" />
                                </PrescriptionFileRemove>
                            </PrescriptionFilePreview>

                            <input ref="prescriptionFileInput" type="file" accept=".pdf,.jpg,.jpeg,.png"
                                style="display: none" @change="handlePrescriptionFileChange" />
                        </PrescriptionDropZone>
                    </PrescriptionUploadContainer>

                    <!-- 당뇨소모성재료 처방전 폼 -->
                    <PrescriptionForm v-if="showDiabetesOptions">
                        <FormRow>
                            <FormGroup>
                                <FormLabel>공인인증서 사용자이름</FormLabel>
                                <PrescriptionInput v-model="prescriptionData.certificateName"
                                    placeholder="공인인증서 사용자이름을 입력하세요" />
                            </FormGroup>
                            <FormGroup>
                                <FormLabel>공인인증서 비밀번호</FormLabel>
                                <PrescriptionInput v-model="prescriptionData.cert_password" type="password"
                                    placeholder="공인인증서 비밀번호를 입력하세요" />
                            </FormGroup>
                            <FormGroup>
                                <FormLabel>성명</FormLabel>
                                <PrescriptionInput v-model="prescriptionData.name" placeholder="성명을 입력하세요" />
                            </FormGroup>
                        </FormRow>

                        <FormRow>
                            <FormGroup>
                                <FormLabel>주민등록번호</FormLabel>
                                <PrescriptionInput v-model="prescriptionData.residentNumber"
                                    @input="formatResidentNumber" placeholder="주민등록번호를 입력하세요" maxlength="14" />
                            </FormGroup>
                            <FormGroup>
                                <FormLabel>전화번호</FormLabel>
                                <PrescriptionInput v-model="prescriptionData.phone" @input="formatPhoneNumber"
                                    placeholder="전화번호를 입력하세요" maxlength="13" />
                            </FormGroup>
                            <FormGroup>
                                <FormLabel>처방전 발행일</FormLabel>
                                <Datepicker v-model:value="prescriptionData.prescriptionDate" placeholder="처방일을 선택하세요"
                                    style="width: 100%;" />
                            </FormGroup>
                        </FormRow>

                        <FormRow>
                            <FormGroup>
                                <FormLabel>당뇨구분</FormLabel>
                                <PrescriptionSelect v-model="prescriptionData.diabetesType">
                                    <option value="">당뇨구분을 선택하세요</option>
                                    <option value="제1형당뇨">제1형당뇨</option>
                                    <option value="제2형당뇨">제2형당뇨</option>
                                    <option value="임신중당뇨">임신중당뇨</option>
                                </PrescriptionSelect>
                            </FormGroup>
                            <FormGroup>
                                <FormLabel>인슐린투여</FormLabel>
                                <PrescriptionSelect v-model="prescriptionData.insulinType">
                                    <option value="">인슐린투여를 선택하세요</option>
                                    <option value="투여">투여</option>
                                    <option value="미투여">미투여</option>
                                </PrescriptionSelect>
                            </FormGroup>
                            <FormGroup>
                                <FormLabel>상병코드</FormLabel>
                                <PrescriptionInput v-model="prescriptionData.diseaseCode"
                                    placeholder="' . ' 빼고 입력하세요" />
                            </FormGroup>
                        </FormRow>

                        <FormRow>
                            <FormGroup>
                                <FormLabel>총 처방기간</FormLabel>
                                <PrescriptionInput v-model="prescriptionData.paymentDays" type="number"
                                    placeholder="총 처방기간를 입력하세요" />
                            </FormGroup>
                            <FormGroup>
                                <FormLabel>혈당검사횟수</FormLabel>
                                <PrescriptionInput v-model="prescriptionData.bloodSugarCount" type="number"
                                    placeholder="혈당검사횟수를 입력하세요" />
                            </FormGroup>
                            <FormGroup>
                                <FormLabel>인슐린주사횟수</FormLabel>
                                <PrescriptionInput v-model="prescriptionData.insulinInjectionCount" type="number"
                                    placeholder="인슐린주사횟수를 입력하세요" />
                            </FormGroup>
                        </FormRow>

                        <FormRow>
                            <FormGroup>
                                <FormLabel>의사면허번호</FormLabel>
                                <PrescriptionInput v-model="prescriptionData.doctorLicense"
                                    placeholder="의사면허번호를 입력하세요" />
                            </FormGroup>
                            <FormGroup>
                                <FormLabel>전문의자격번호</FormLabel>
                                <PrescriptionInput v-model="prescriptionData.specialistLicense"
                                    placeholder="전문의자격번호를 입력하세요" />
                            </FormGroup>
                        </FormRow>

                        <FormRow>
                            <FormGroup>
                                <FormLabel>구입일</FormLabel>
                                <Datepicker v-model:value="prescriptionData.purchaseDate" placeholder="구입일을 선택하세요"
                                    style="width: 100%;" />
                            </FormGroup>
                            <FormGroup>
                                <FormLabel>사용개시일</FormLabel>
                                <Datepicker v-model:value="prescriptionData.useStartDate" placeholder="사용개시일을 선택하세요"
                                    style="width: 100%;" />
                            </FormGroup>
                        </FormRow>
                    </PrescriptionForm>
                </RightSection>
            </ClaimInfoMain>

            <!-- 하단 버튼 -->
            <ClaimInfoFooter>
                <CancelButton @click="$emit('close')">취소</CancelButton>
                <SaveButton @click="saveClaimInfo">
                    {{ selectedClaimIndex >= 0 ? '저장' : '등록' }}
                </SaveButton>
            </ClaimInfoFooter>
        </ClaimInfoContainer>
    </ModalContent>
</template>

<script>
import * as ClaimInfoModalCss from '@/assets/styles/order/modal/ClaimInfoModalCss.js';

import CloseIcon from 'vue-material-design-icons/Close.vue';
import FileDocumentIcon from 'vue-material-design-icons/FileDocument.vue';
import RefreshIcon from 'vue-material-design-icons/Refresh.vue';
import PlusIcon from 'vue-material-design-icons/Plus.vue';
import EditIcon from 'vue-material-design-icons/Pencil.vue';
import DeleteIcon from 'vue-material-design-icons/Delete.vue';
import Datepicker from 'vue-datepicker-next';
import InformationOutline from 'vue-material-design-icons/InformationOutline.vue';

export default {
    name: 'DiabetesClaimInfoModal',

    components: {
        ...ClaimInfoModalCss,
        CloseIcon,
        FileDocumentIcon,
        RefreshIcon,
        PlusIcon,
        EditIcon,
        DeleteIcon,
        Datepicker,
        InformationOutline
    },

    props: {
        orderId: {
            type: [String, Number],
            required: true
        },
        orderData: {
            type: Object,
            default: () => ({})
        },
        claimType: {
            type: String,
            default: 'diabetes'
        },
        products: {
            type: Array,
            default: () => []
        },
        rentalTypes: {
            type: Object,
            default: () => ({
                hasRentalProduct: false,
                hasOxygenGenerator: false,
                hasDiabetesProduct: false,
                hasAssistanceProduct: false
            })
        },
        memberData: {
            type: Object,
            default: () => ({})
        },
        additionalData: {
            type: Object,
            default: () => ({})
        }
    },

    emits: ['close', 'claim-info-saved'],

    data() {
        return {
            prescriptionFile: null,
            isPrescriptionDragging: false,
            showDiabetesOptions: true,
            selectedClaimIndex: -1,
            isOcrAnalyzing: false,

            // 청구 이력 데이터
            claimHistory: [],

            prescriptionData: {
                certificateName: '',
                cert_password: '',
                name: '',
                residentNumber: '',
                phone: '',
                prescriptionDate: null,
                diabetesType: '',
                insulinType: '',
                doctorLicense: '',
                specialistLicense: '',
                diseaseCode: '',
                bloodSugarCount: '',
                insulinInjectionCount: '',
                purchaseDate: null,
                useStartDate: null,
                paymentDays: ''
            }
        };
    },

    mounted() {
        this.initializeData();
        this.loadClaimHistory();
    },

    methods: {
        initializeData() {
            // 주문 데이터에서 기본 정보 설정
            if (this.orderData) {
                // 수급자 기본 정보
                this.prescriptionData.name = this.orderData.name || '';
                this.prescriptionData.phone = this.formatPhoneWithHyphens(this.orderData.phone1 || '');
                this.prescriptionData.residentNumber = this.formatResidentNumberWithHyphens(this.orderData.number || '');
            }

            // 추가 데이터에서 정보 추출
            if (this.additionalData) {
                const clientDetails = this.additionalData.clientDetails || {};
                const productInfo = this.additionalData.productInfo || {};
                const claimInfo = this.additionalData.claimInfo || {};

                // 수급자 상세 정보
                if (clientDetails.name && !this.prescriptionData.name) {
                    this.prescriptionData.name = clientDetails.name;
                }
                if (clientDetails.phone1 && !this.prescriptionData.phone) {
                    this.prescriptionData.phone = this.formatPhoneWithHyphens(clientDetails.phone1);
                }
                if (clientDetails.resident && !this.prescriptionData.residentNumber) {
                    this.prescriptionData.residentNumber = this.formatResidentNumberWithHyphens(clientDetails.resident);
                }

                // 당뇨치료 관련 정보
                if (clientDetails.prescription_date) {
                    this.prescriptionData.prescriptionDate = new Date(clientDetails.prescription_date);
                }
                if (clientDetails.diabetes_type) {
                    this.prescriptionData.diabetesType = clientDetails.diabetes_type;
                }
                if (clientDetails.insulin_type) {
                    this.prescriptionData.insulinType = clientDetails.insulin_type;
                }
                if (clientDetails.blood_sugar_count) {
                    this.prescriptionData.bloodSugarCount = clientDetails.blood_sugar_count;
                }
                if (clientDetails.insulin_injection_count) {
                    this.prescriptionData.insulinInjectionCount = clientDetails.insulin_injection_count;
                }
                if (clientDetails.purchase_date) {
                    this.prescriptionData.purchaseDate = new Date(clientDetails.purchase_date);
                }
                if (clientDetails.use_start_date) {
                    this.prescriptionData.useStartDate = new Date(clientDetails.use_start_date);
                }
                if (clientDetails.payment_days) {
                    this.prescriptionData.paymentDays = clientDetails.payment_days;
                }
            }
        },

        // 주민등록번호에 하이픈 추가 (통합)
        formatResidentNumberWithHyphens(value) {
            if (!value) return '';

            // 숫자만 추출
            const numbers = value.replace(/[^0-9]/g, '');

            if (numbers.length >= 7) {
                return numbers.substring(0, 6) + '-' + numbers.substring(6, 13);
            } else if (numbers.length >= 6) {
                return numbers.substring(0, 6) + '-' + numbers.substring(6);
            }

            return numbers;
        },

        // 전화번호에 하이픈 추가 (통합)
        formatPhoneWithHyphens(value) {
            if (!value) return '';

            // 숫자만 추출
            const numbers = value.replace(/[^0-9]/g, '');

            if (numbers.length >= 11) {
                return numbers.substring(0, 3) + '-' + numbers.substring(3, 7) + '-' + numbers.substring(7, 11);
            } else if (numbers.length >= 7) {
                return numbers.substring(0, 3) + '-' + numbers.substring(3, 7) + '-' + numbers.substring(7);
            } else if (numbers.length >= 3) {
                return numbers.substring(0, 3) + '-' + numbers.substring(3);
            }

            return numbers;
        },

        // 주민등록번호 포맷팅 (사용자 입력용)
        formatResidentNumber(event) {
            this.prescriptionData.residentNumber = this.formatResidentNumberWithHyphens(event.target.value);
        },

        // 전화번호 포맷팅 (사용자 입력용)
        formatPhoneNumber(event) {
            this.prescriptionData.phone = this.formatPhoneWithHyphens(event.target.value);
        },

        // "-" 제거 함수
        removeHyphens(value) {
            return value ? value.replace(/-/g, '') : '';
        },

        async loadClaimHistory() {
            try {
                const response = await this.$axios.get(`${this.$apiUrls.hanqmedi}/diabetesClaimData/${this.orderId}`);
                if (response.data && response.data.success) {

                    this.claimHistory = response.data.data.map((claim, index) => {
                        const currentStep = this.getCurrentStep(claim);

                        return {
                            id: claim.num,
                            title: `${claim.diabetes_type} 청구`,
                            status: currentStep.status,
                            statusText: currentStep.text,
                            diabetesType: claim.diabetes_type || '',
                            prescriptionDate: this.formatDate(claim.diabetes_date), // diabetes_date로 수정
                            createdDate: this.formatDate(claim.created_at),
                            data: {
                                prescriptionData: {
                                    certificateName: claim.certificate_id || '',
                                    cert_password: claim.certificate_pw || '',
                                    name: claim.name || '',
                                    residentNumber: this.formatResidentNumberWithHyphens(claim.number || ''),
                                    phone: this.formatPhoneWithHyphens(claim.phone || ''),
                                    prescriptionDate: claim.diabetes_date ? new Date(claim.diabetes_date) : null, // diabetes_date로 수정
                                    diabetesType: claim.diabetes_type || '',
                                    insulinType: claim.insulin_type || '',
                                    doctorLicense: claim.doctor_number || '', // doctor_number로 수정
                                    specialistLicense: claim.specialist_number || '', // specialist_number로 수정
                                    diseaseCode: claim.disease_code || '',
                                    bloodSugarCount: claim.blood_count || '', // blood_count로 수정
                                    insulinInjectionCount: claim.insulin_count || '', // insulin_count로 수정
                                    purchaseDate: claim.purchase_date ? new Date(claim.purchase_date) : null,
                                    useStartDate: claim.use_start_date ? new Date(claim.use_start_date) : null,
                                    paymentDays: claim.payment_days || ''
                                }
                            }
                        };
                    });

                    // 첫 번째 청구가 있으면 자동으로 로드 (테스트용)
                    if (this.claimHistory.length > 0) {
                        this.selectClaim(this.claimHistory[0], 0);
                    }
                }
            } catch (error) {
                console.error('청구 이력 로드 오류:', error);
            }
        },

        loadClaimData(claim) {
            if (!claim.data || !claim.data.prescriptionData) {
                console.error('청구 데이터가 없습니다:', claim);
                return;
            }

            const prescriptionData = claim.data.prescriptionData;

            // Vue의 반응성을 위해 $nextTick 사용
            this.$nextTick(() => {
                // 나머지 필드들도 설정
                this.prescriptionData.certificateName = prescriptionData.certificateName || '';
                this.prescriptionData.cert_password = prescriptionData.cert_password || '';
                this.prescriptionData.name = prescriptionData.name || '';
                this.prescriptionData.residentNumber = prescriptionData.residentNumber || '';
                this.prescriptionData.phone = prescriptionData.phone || '';
                this.prescriptionData.prescriptionDate = prescriptionData.prescriptionDate || null;
                this.prescriptionData.diabetesType = prescriptionData.diabetesType || '';
                this.prescriptionData.insulinType = prescriptionData.insulinType || '';
                this.prescriptionData.doctorLicense = prescriptionData.doctorLicense || '';
                this.prescriptionData.specialistLicense = prescriptionData.specialistLicense || '';
                this.prescriptionData.diseaseCode = prescriptionData.diseaseCode || '';
                this.prescriptionData.bloodSugarCount = prescriptionData.bloodSugarCount || '';
                this.prescriptionData.insulinInjectionCount = prescriptionData.insulinInjectionCount || '';
                this.prescriptionData.purchaseDate = prescriptionData.purchaseDate || null;
                this.prescriptionData.useStartDate = prescriptionData.useStartDate || null;
                this.prescriptionData.paymentDays = prescriptionData.paymentDays || '';

                // 강제로 UI 업데이트 트리거
                this.$forceUpdate();
            });
        },

        saveClaimInfo() {
            const formatDateForAPI = (date) => {
                if (!date) return null;
                if (date instanceof Date) {
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                }
                return date;
            };

            const saveData = {
                orderId: this.orderId,
                certificateName: this.prescriptionData.certificateName,
                certPassword: this.prescriptionData.cert_password,
                name: this.prescriptionData.name,
                residentNumber: this.removeHyphens(this.prescriptionData.residentNumber),
                phone: this.removeHyphens(this.prescriptionData.phone),
                prescriptionDate: formatDateForAPI(this.prescriptionData.prescriptionDate),
                diabetesType: this.prescriptionData.diabetesType,
                insulinType: this.prescriptionData.insulinType,
                doctorLicense: this.prescriptionData.doctorLicense,
                specialistLicense: this.prescriptionData.specialistLicense,
                diseaseCode: this.prescriptionData.diseaseCode,
                bloodSugarCount: this.prescriptionData.bloodSugarCount,
                insulinInjectionCount: this.prescriptionData.insulinInjectionCount,
                purchaseDate: formatDateForAPI(this.prescriptionData.purchaseDate),
                useStartDate: formatDateForAPI(this.prescriptionData.useStartDate),
                paymentDays: this.prescriptionData.paymentDays
            };

            this.saveClaimDataToServer(saveData);
        },

        // 현재 진행단계 결정
        getCurrentStep(claim) {
            // 실제로는 데이터베이스의 상태 필드나 다른 로직을 사용해야 합니다
            // 여기서는 예시로 생성일 기준으로 단계를 결정합니다

            const createdDate = new Date(claim.created_at);
            const now = new Date();
            const daysDiff = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24));

            if (daysDiff < 1) {
                return { status: 'info', text: '청구정보저장' };
            } else if (daysDiff < 3) {
                return { status: 'warning', text: '환자등록' };
            } else if (daysDiff < 7) {
                return { status: 'warning', text: '위임내역등록' };
            } else {
                return { status: 'success', text: '청구등록' };
            }
        },

        // 청구 이력 새로고침
        refreshClaimHistory() {
            this.loadClaimHistory();
        },

        // 선택된 청구 삭제
        async deleteSelectedClaim() {
            if (this.selectedClaimIndex < 0) {
                this.$swal.fire({
                    icon: 'warning',
                    title: '청구를 선택해주세요',
                    text: '삭제할 청구 이력을 먼저 선택해주세요.',
                    confirmButtonText: '확인'
                });
                return;
            }

            const selectedClaim = this.claimHistory[this.selectedClaimIndex];

            const result = await this.$swal.fire({
                title: '청구 삭제',
                text: `"${selectedClaim.title}" 청구를 삭제하시겠습니까?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: '삭제',
                cancelButtonText: '취소',
                confirmButtonColor: '#ef4444',
                cancelButtonColor: '#6b7280'
            });

            if (result.isConfirmed) {
                try {
                    // API 호출로 청구 삭제
                    await this.$axios.delete(`${this.$apiUrls.hanqmedi}/diabetesClaimData/${selectedClaim.id}`);

                    // 청구 이력에서 제거
                    this.claimHistory.splice(this.selectedClaimIndex, 1);

                    // 선택 해제 및 폼 초기화
                    this.selectedClaimIndex = -1;
                    this.resetForm();

                    this.$swal.fire({
                        icon: 'success',
                        title: '삭제 완료',
                        text: '선택한 청구가 성공적으로 삭제되었습니다.',
                        confirmButtonText: '확인'
                    });
                } catch (error) {
                    console.error('청구 삭제 오류:', error);
                    this.$swal.fire({
                        icon: 'error',
                        title: '삭제 실패',
                        text: '삭제 중 오류가 발생했습니다.',
                        confirmButtonText: '확인'
                    });
                }
            }
        },

        // 청구 항목 선택
        selectClaim(claim, index) {
            // 이미 선택된 항목을 다시 클릭하면 선택 해제
            if (this.selectedClaimIndex === index) {
                this.selectedClaimIndex = -1;
                this.resetForm();
                return;
            }

            this.selectedClaimIndex = index;
            // 선택된 청구 데이터를 폼에 로드
            this.loadClaimData(claim);
        },

        // 파일 드래그 앤 드롭 핸들러
        handlePrescriptionDragEnter(e) {
            e.preventDefault();
            this.isPrescriptionDragging = true;
        },

        handlePrescriptionDragOver(e) {
            e.preventDefault();
        },

        handlePrescriptionDragLeave(e) {
            e.preventDefault();
            this.isPrescriptionDragging = false;
        },

        handlePrescriptionDrop(e) {
            e.preventDefault();
            this.isPrescriptionDragging = false;
            const files = e.dataTransfer.files;
            if (files.length > 0) {
                this.handlePrescriptionFile(files[0]);
            }
        },

        handlePrescriptionPaste(e) {
            const items = e.clipboardData.items;
            for (let item of items) {
                if (item.type.indexOf('image') !== -1) {
                    const file = item.getAsFile();
                    this.handlePrescriptionFile(file);
                    break;
                }
            }
        },

        triggerPrescriptionFileInput() {
            if (this.$refs.prescriptionFileInput) {
                this.$refs.prescriptionFileInput.click();
            }
        },

        handlePrescriptionFileChange(e) {
            const file = e.target.files[0];
            if (file) {
                this.handlePrescriptionFile(file);
            }
        },

        handlePrescriptionFile(file) {
            if (file && (file.type === 'application/pdf' || file.type.startsWith('image/'))) {
                this.prescriptionFile = file;
            } else {
                this.$swal.fire({
                    icon: 'error',
                    title: '파일 형식 오류',
                    text: 'PDF, JPG, PNG 파일만 업로드 가능합니다.',
                    confirmButtonText: '확인'
                });
            }
        },

        removePrescriptionFile() {
            this.prescriptionFile = null;
        },

        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },

        // OCR 분석 시작
        async analyzePrescription() {
            if (!this.prescriptionFile) {
                this.$swal.fire({
                    icon: 'warning',
                    title: '파일 없음',
                    text: '처방전 파일을 먼저 업로드해주세요.',
                    confirmButtonText: '확인'
                });
                return;
            }

            this.isOcrAnalyzing = true;

            try {
                // 파일 타입에 따른 처리
                let fileToSend = this.prescriptionFile;

                // PDF 파일인 경우 크기 체크
                if (this.prescriptionFile.type === 'application/pdf') {
                    if (this.prescriptionFile.size > 30 * 1024 * 1024) { // 30MB 제한
                        this.$swal.fire({
                            icon: 'error',
                            title: '파일 크기 초과',
                            text: 'PDF 파일 크기가 30MB를 초과합니다. 더 작은 파일을 업로드해주세요.',
                            confirmButtonText: '확인'
                        });
                        return;
                    }
                } else {
                    // 이미지 파일인 경우 압축 처리
                    if (this.prescriptionFile.size > 10 * 1024 * 1024) { // 10MB 초과시 압축
                        try {
                            fileToSend = await this.compressImage(this.prescriptionFile);
                        } catch (compressError) {
                            fileToSend = this.prescriptionFile;
                        }
                    }
                }

                // FormData 생성
                const formData = new FormData();

                // 압축된 이미지인 경우 Blob으로 변환
                if (typeof fileToSend === 'string' && fileToSend.startsWith('data:')) {
                    // base64 데이터를 Blob으로 변환
                    const response = await fetch(fileToSend);
                    const blob = await response.blob();
                    formData.append('file', blob, this.prescriptionFile.name);
                } else {
                    formData.append('file', fileToSend);
                }

                // 청구 유형 추가
                formData.append('claimType', this.claimType);

                // API URL 설정
                let apiUrl;
                try {
                    const config = this.$getEnvironmentConfig ? this.$getEnvironmentConfig() : null;
                    apiUrl = config ? `${config.hanqmedi}/processOcr` : `${this.$apiUrls.hanqmedi}/processOcr`;
                } catch (error) {
                    apiUrl = `${this.$apiUrls.hanqmedi}/processOcr`;
                }

                // fetch API를 사용하여 FormData 전송
                const response = await fetch(apiUrl, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: formData
                });

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }

                const responseData = await response.json();
                console.log('OCR 응답:', responseData);

                if (responseData.success) {
                    this.processOcrResult(responseData.data);
                    this.$swal.fire({
                        icon: 'success',
                        title: '분석 완료',
                        text: '처방전 데이터가 성공적으로 분석되었습니다.',
                        confirmButtonText: '확인'
                    });
                } else {
                    throw new Error(responseData.message || 'OCR 분석에 실패했습니다.');
                }
            } catch (error) {
                console.error('OCR 분석 오류:', error);

                let errorMessage = 'OCR 분석 중 오류가 발생했습니다.';
                if (error.message) {
                    errorMessage = error.message;
                }

                this.$swal.fire({
                    icon: 'error',
                    title: '분석 실패',
                    text: errorMessage,
                    confirmButtonText: '확인'
                });
            } finally {
                this.isOcrAnalyzing = false;
            }
        },

        // 이미지 압축 함수
        async compressImage(file) {
            return new Promise((resolve) => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                const img = new Image();

                img.onload = () => {
                    const maxWidth = 1920;
                    const maxHeight = 1080;
                    let { width, height } = img;

                    if (width > height) {
                        if (width > maxWidth) {
                            height = (height * maxWidth) / width;
                            width = maxWidth;
                        }
                    } else {
                        if (height > maxHeight) {
                            width = (width * maxHeight) / height;
                            height = maxHeight;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;

                    ctx.drawImage(img, 0, 0, width, height);
                    canvas.toBlob(resolve, 'image/jpeg', 0.8);
                };

                img.src = URL.createObjectURL(file);
            });
        },

        // OCR 결과 처리
        processOcrResult(data) {
            // OCR 결과 구조 확인 및 안전한 처리
            let fields = [];
            if (data && Array.isArray(data)) {
                fields = data;
            } else if (data && data.fields && Array.isArray(data.fields)) {
                fields = data.fields;
            } else if (data && data.images && data.images[0] && data.images[0].fields) {
                fields = data.images[0].fields;
            } else if (data && typeof data === 'object') {
                // 단일 객체인 경우 처리
                this.processSingleOcrResult(data);
                return;
            }

            if (fields.length > 0) {
                // 당뇨치료 처방전 데이터 매칭
                this.matchDiabetesPrescriptionData(fields);
            } else {
                this.$swal.fire({
                    icon: 'warning',
                    title: '분석 결과 없음',
                    text: '처방전에서 분석할 수 있는 데이터를 찾을 수 없습니다.',
                    confirmButtonText: '확인'
                });
            }
        },

        // 단일 OCR 결과 처리
        processSingleOcrResult(data) {
            // 데이터를 직접 매핑
            this.prescriptionData.name = data.name || this.prescriptionData.name;
            this.prescriptionData.residentNumber = this.formatResidentNumberWithHyphens(data.residentNumber || this.prescriptionData.residentNumber);
            this.prescriptionData.phone = this.formatPhoneWithHyphens(data.phone || this.prescriptionData.phone);
            this.prescriptionData.diabetesType = data.diabetesType || this.prescriptionData.diabetesType;
            this.prescriptionData.insulinType = data.insulinType || this.prescriptionData.insulinType;
            this.prescriptionData.doctorLicense = data.doctorLicense || this.prescriptionData.doctorLicense;
            this.prescriptionData.specialistLicense = data.specialistLicense || this.prescriptionData.specialistLicense;
            this.prescriptionData.diseaseCode = data.diseaseCode || this.prescriptionData.diseaseCode;
            this.prescriptionData.bloodSugarCount = data.bloodSugarCount || this.prescriptionData.bloodSugarCount;
            this.prescriptionData.insulinInjectionCount = data.insulinInjectionCount || this.prescriptionData.insulinInjectionCount;
        },

        // 당뇨치료 처방전 데이터 매칭
        matchDiabetesPrescriptionData(fields) {
            const matchedFields = new Set();

            // 필드 매핑 정의
            const fieldMappings = {
                '성명': 'name',
                '주민등록번호': 'residentNumber',
                '전화번호': 'phone',
                '당뇨구분': 'diabetesType',
                '인슐린투여': 'insulinType',
                '의사면허번호': 'doctorLicense',
                '전문의자격번호': 'specialistLicense',
                '상병코드': 'diseaseCode',
                '혈당검사횟수': 'bloodSugarCount',
                '인슐린주사횟수': 'insulinInjectionCount',
                '처방일자': 'prescriptionDate',
                '구입일': 'purchaseDate',
                '사용개시일': 'useStartDate',
                '총 처방기간': 'paymentDays',
                '수진자': 'name',
                '수진자명': 'name',
                '환자명': 'name',
                '면허번호': 'doctorLicense',
                '의사번호': 'doctorLicense',
                '전문의자격번호': 'specialistLicense',
                '전문의': 'specialistLicense'
            };

            // 기본 필드 매칭
            fields.forEach((field, index) => {
                const fieldName = field.name || `필드 ${index + 1}`;
                const fieldText = field.inferText || '';

                console.log(`${fieldName}: ${fieldText}`);

                const targetField = fieldMappings[fieldName];
                if (targetField && !matchedFields.has(targetField)) {
                    const value = this.extractFieldValue(fieldText, targetField);
                    if (value) {
                        // 주민등록번호와 전화번호는 하이픈 추가
                        if (targetField === 'residentNumber') {
                            this.prescriptionData[targetField] = this.formatResidentNumberWithHyphens(value);
                        } else if (targetField === 'phone') {
                            this.prescriptionData[targetField] = this.formatPhoneWithHyphens(value);
                        } else {
                            this.prescriptionData[targetField] = value;
                        }
                        matchedFields.add(targetField);
                        console.log(`✅ ${fieldName}에서 ${targetField} 찾음: ${this.prescriptionData[targetField]}`);
                    }
                }
            });
        },

        // 필드 값 추출 개선
        extractFieldValue(text, fieldType) {
            if (!text || typeof text !== 'string') return null;

            switch (fieldType) {
                case 'name':
                    return this.extractName(text);
                case 'residentNumber':
                    return this.extractResidentNumber(text);
                case 'phone':
                    return this.extractPhone(text);
                case 'diabetesType':
                    return this.extractDiabetesType(text);
                case 'insulinType':
                    return this.extractInsulinType(text);
                case 'doctorLicense':
                    return this.extractDoctorLicense(text);
                case 'specialistLicense':
                    return this.extractSpecialistLicense(text);
                case 'diseaseCode':
                    return this.extractDiseaseCode(text);
                case 'bloodSugarCount':
                    return this.extractBloodSugarCount(text);
                case 'insulinInjectionCount':
                    return this.extractInsulinInjectionCount(text);
                case 'prescriptionDate':
                    return this.extractPrescriptionDate(text);
                case 'purchaseDate':
                    return this.extractPurchaseDate(text);
                case 'useStartDate':
                    return this.extractUseStartDate(text);
                case 'paymentDays':
                    return this.extractPaymentDays(text);
                default:
                    return text.trim();
            }
        },

        // 성명 추출 개선
        extractName(text) {
            // "성명\n지수자" 패턴
            const nameMatch = text.match(/성명\s*\n\s*([가-힣]{2,4})/);
            if (nameMatch) return nameMatch[1];

            // "성명 OOO" 패턴
            const nameMatch2 = text.match(/성명\s*([가-힣]{2,4})/);
            if (nameMatch2) return nameMatch2[1];

            // "수진자 성명 OOO" 패턴
            const patientNameMatch = text.match(/수진자\s*성명\s*([가-힣]{2,4})/);
            if (patientNameMatch) return patientNameMatch[1];

            // "수진자 OOO" 패턴
            const patientMatch = text.match(/수진자\s+([가-힣]{2,4})(?=\s|$)/);
            if (patientMatch) return patientMatch[1];

            // "환자명 OOO" 패턴
            const patientNameMatch2 = text.match(/환자명\s*([가-힣]{2,4})/);
            if (patientNameMatch2) return patientNameMatch2[1];

            // 한글 이름만 추출 (2-4자)
            const koreanNameMatch = text.match(/([가-힣]{2,4})/);
            if (koreanNameMatch) return koreanNameMatch[1];

            return null;
        },

        // 주민등록번호 추출 개선
        extractResidentNumber(text) {
            // "주민등록번호(외국인등록번호)\n541020-2631917" 패턴
            const match1 = text.match(/주민등록번호[^\n]*\n\s*(\d{6})-(\d{7})/);
            if (match1) return match1[1] + match1[2];

            // (공백으로 구분된 주민번호)
            const match2 = text.match(/(\d{6})\s+(\d{7})/);
            if (match2) return match2[1] + match2[2];

            // (하이픈으로 구분된 주민번호)
            const match3 = text.match(/(\d{6})-(\d{7})/);
            if (match3) return match3[1] + match3[2];

            // 연속된 13자리 숫자 패턴
            const match4 = text.match(/(\d{6})(\d{7})/);
            if (match4) return match4[1] + match4[2];

            return null;
        },

        // 전화번호 추출 개선
        extractPhone(text) {
            // 휴대전화 우선 추출
            const mobileMatch = text.match(/휴대전화[^\d]*(\d{3})-(\d{4})-(\d{4})/);
            if (mobileMatch) return mobileMatch[1] + mobileMatch[2] + mobileMatch[3];

            // 자택 전화번호
            const homeMatch = text.match(/자택[^\d]*(\d{3})-(\d{3,4})-(\d{4})/);
            if (homeMatch) return homeMatch[1] + homeMatch[2] + homeMatch[3];

            // 하이픈이 있는 전화번호
            const match1 = text.match(/(\d{3})-(\d{3,4})-(\d{4})/);
            if (match1) return match1[1] + match1[2] + match1[3];

            // 공백이 있는 전화번호
            const match2 = text.match(/(\d{3})\s+(\d{3,4})\s+(\d{4})/);
            if (match2) return match2[1] + match2[2] + match2[3];

            // 연속된 10-11자리 숫자
            const match3 = text.match(/(\d{10,11})/);
            return match3 ? match3[1] : null;
        },

        // 인슐린투여 추출
        extractInsulinType(text) {
            if (text.includes('투여')) return '투여';
            if (text.includes('미투여')) return '미투여';
            return null;
        },

        // 의사면허번호 추출 개선
        extractDoctorLicense(text) {
            // "면허번호 제 92890 호" 패턴
            const match1 = text.match(/면허번호\s*제\s*(\d+)\s*호/);
            if (match1) return match1[1];

            // "면허번호 92890" 패턴
            const match2 = text.match(/면허번호\s*(\d+)/);
            if (match2) return match2[1];

            // "의사면허번호 92890" 패턴
            const match3 = text.match(/의사면허번호\s*(\d+)/);
            if (match3) return match3[1];

            // "의사번호 92890" 패턴
            const match4 = text.match(/의사번호\s*(\d+)/);
            if (match4) return match4[1];

            // 숫자만 추출
            const match5 = text.match(/(\d+)/);
            if (match5) return match5[1];

            return null;
        },

        // 전문의 자격번호 추출 개선
        extractSpecialistLicense(text) {
            // "전문의 자격번호 16061" 패턴
            const match1 = text.match(/전문의\s*자격번호\s*(\d+)/);
            if (match1) return match1[1];

            // "자격번호 16061" 패턴
            const match2 = text.match(/자격번호\s*(\d+)/);
            if (match2) return match2[1];

            // "전문의 16061" 패턴
            const match3 = text.match(/전문의\s*(\d+)/);
            if (match3) return match3[1];

            // 숫자만 추출
            const match4 = text.match(/(\d+)/);
            if (match4) return match4[1];

            return null;
        },

        // 상병코드 추출 개선
        extractDiseaseCode(text) {
            // "상병코드\n150.0" 패턴
            const match1 = text.match(/상병코드\s*\n\s*([0-9.]+)/);
            if (match1) return match1[1].trim();

            // "상병코드 OOO" 패턴
            const match2 = text.match(/상병코드\s*([0-9.]+)/);
            if (match2) return match2[1].trim();

            // "질병코드 OOO" 패턴
            const match3 = text.match(/질병코드\s*([0-9.]+)/);
            if (match3) return match3[1].trim();

            // 숫자와 점으로 구성된 코드 패턴
            const match4 = text.match(/([0-9]+\.?[0-9]*)/);
            if (match4) return match4[1];

            return null;
        },

        // 혈당검사횟수 추출
        extractBloodSugarCount(text) {
            // "혈당검사횟수 3회" 패턴
            const match1 = text.match(/혈당검사횟수\s*(\d+)\s*회/);
            if (match1) return match1[1];

            // "혈당검사 3회" 패턴
            const match2 = text.match(/혈당검사\s*(\d+)\s*회/);
            if (match2) return match2[1];

            // 숫자만 추출
            const match3 = text.match(/(\d+)/);
            if (match3) return match3[1];

            return null;
        },

        // 인슐린주사횟수 추출
        extractInsulinInjectionCount(text) {
            // "인슐린주사횟수 2회" 패턴
            const match1 = text.match(/인슐린주사횟수\s*(\d+)\s*회/);
            if (match1) return match1[1];

            // "인슐린주사 2회" 패턴
            const match2 = text.match(/인슐린주사\s*(\d+)\s*회/);
            if (match2) return match2[1];

            // 숫자만 추출
            const match3 = text.match(/(\d+)/);
            if (match3) return match3[1];

            return null;
        },

        // 처방일자 추출 개선
        extractPrescriptionDate(text) {
            // "2025 년 08 월 26일" 패턴
            const match1 = text.match(/(\d{4})\s*년\s*(\d{1,2})\s*월\s*(\d{1,2})\s*일/);
            if (match1) {
                const year = match1[1];
                const month = match1[2].padStart(2, '0');
                const day = match1[3].padStart(2, '0');
                return `${year}-${month}-${day}`;
            }

            // "2025-08-26" 패턴
            const match2 = text.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
            if (match2) {
                const year = match2[1];
                const month = match2[2].padStart(2, '0');
                const day = match2[3].padStart(2, '0');
                return `${year}-${month}-${day}`;
            }

            // "2025.08.26" 패턴
            const match3 = text.match(/(\d{4})\.(\d{1,2})\.(\d{1,2})/);
            if (match3) {
                const year = match3[1];
                const month = match3[2].padStart(2, '0');
                const day = match3[3].padStart(2, '0');
                return `${year}-${month}-${day}`;
            }

            return null;
        },

        // 구입일 추출
        extractPurchaseDate(text) {
            return this.extractPrescriptionDate(text); // 동일한 패턴 사용
        },

        // 사용개시일 추출
        extractUseStartDate(text) {
            return this.extractPrescriptionDate(text); // 동일한 패턴 사용
        },

        // 총 처방기간 추출
        extractPaymentDays(text) {
            // "총 처방기간 30일" 패턴
            const match1 = text.match(/총 처방기간\s*(\d+)\s*일/);
            if (match1) return match1[1];

            // "30일" 패턴
            const match2 = text.match(/(\d+)\s*일/);
            if (match2) return match2[1];

            // 숫자만 추출
            const match3 = text.match(/(\d+)/);
            if (match3) return match3[1];

            return null;
        },

        // 서버에 청구 데이터 저장
        async saveClaimDataToServer(saveData) {
            try {
                let response;
                if (this.selectedClaimIndex >= 0) {
                    // 수정 - 올바른 API URL 사용
                    const claimId = this.claimHistory[this.selectedClaimIndex].id;
                    response = await this.$axios.put(`${this.$apiUrls.hanqmedi}/diabetesClaimData/${claimId}`, saveData);
                } else {
                    // 생성 - 올바른 API URL 사용
                    response = await this.$axios.post(`${this.$apiUrls.hanqmedi}/diabetesClaimData`, saveData);
                }

                if (response.data.success) {
                    // 상태를 '완료'로 업데이트
                    if (response.data.data.diabetesDataId) {
                        await this.$axios.put(`${this.$apiUrls.hanqmedi}/diabetesClaimStatus/${response.data.data.diabetesDataId}`, {
                            status: '청구정보저장'
                        });
                    }

                    this.$swal.fire({
                        icon: 'success',
                        title: '저장 완료',
                        text: '당뇨소모성재료 청구 정보가 성공적으로 저장되었습니다.'
                    });

                    // 부모 컴포넌트에 저장 완료 이벤트 전달
                    this.$emit('claim-info-saved', {
                        claimType: 'diabetes',
                        data: response.data.data
                    });

                    // 모달 닫기
                    this.$emit('close');
                }

            } catch (error) {
                console.error('당뇨 청구 정보 저장 중 오류:', error);
                this.$swal.fire({
                    icon: 'error',
                    title: '저장 실패',
                    text: '당뇨소모성재료 청구 정보 저장 중 오류가 발생했습니다.'
                });
            }
        },

        // 폼 초기화
        resetForm() {
            this.prescriptionData = {
                certificateName: '',
                cert_password: '',
                name: '',
                residentNumber: '',
                phone: '',
                prescriptionDate: null,
                diabetesType: '',
                insulinType: '',
                doctorLicense: '',
                specialistLicense: '',
                diseaseCode: '',
                bloodSugarCount: '',
                insulinInjectionCount: '',
                purchaseDate: null,
                useStartDate: null,
                paymentDays: ''
            };
            this.prescriptionFile = null;
            this.selectedClaimIndex = -1;
        },

        // 날짜 포맷팅 헬퍼 메서드들
        formatDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            // 한국 시간대로 표시
            return date.toLocaleDateString('ko-KR', {
                timeZone: 'Asia/Seoul',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            });
        }
    },
};
</script>